import type { FactorTrustUiGroup } from '@/modules/booking/hooks/factor/useFactorTrustUiGroup';
import { splunkInstance } from '@/common/services/splunk';
import { useEffect, useState } from 'react';
import type React from 'react';
import { useFactorDoctorData } from '@/modules/booking/hooks/factor/useFactorDoctorData';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { getDoctorCenters } from '@/common/apis/services/doctor/getDoctorCenters';
import {
  validateDoctorSlug,
  DoctorSlugValidationResponse,
} from '@/common/apis/services/doctor/validateDoctorSlug';
import Skeleton from '@/common/components/atom/skeleton';
import Text from '@/common/components/atom/text';
import { CENTERS } from '@/common/types/centers';
import getDisplayDoctorExpertise from '@/common/utils/getDisplayDoctorExpertise';
import { getImageUrl } from '@/common/utils/getImageUrl';
import { picUserImageUrl } from '@/common/utils/picUserImageUrl';
import { useInvoiceNoticeFreeturn } from '@/modules/booking/hooks/factor/useInvoiceNoticeFreeturn';
import DoctorInfo from '@/modules/myTurn/components/doctorInfo';
import { chatLifecycleHoursToDisplayDays, useChatLifecycleAutoCloseHours } from '@/common/apis/services/hami/chatLifecycleSettings';
import { useDoctorOnlineChannels } from '@/common/apis/services/doctor/getDoctorOnlineChannels';
import { getRateDetailsData, mapRateDetailsToCardRate, PROFILE_VISIBLE_RATE_MIN_COUNT, RateDetails } from '@/modules/profile/functions/getRateDetailsData';
import { findSearchDoctor } from '@/modules/profile/utils/seedPiecesFromSearch';
import { priorDoctorData } from '@/modules/booking/hooks/factor/priorDoctorData';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export const DoctorInvoiceNotice = ({
  slug,
  serviceId,
  requestedServiceId,
  userCenterId: userCenterIdProp,
  abTest = { isTrustUi: false, abGroup: null },
}: {
  slug: string;
  serviceId: string;
  /** The URL's service id: usable before the page's availability check confirms `serviceId`. */
  requestedServiceId?: string;
  userCenterId?: string;
  abTest?: FactorTrustUiGroup;
}) => {
  const queryClient = useQueryClient();
  const { fullName: { data: fullName, isLoading: isFullNameLoading }, expertises: { data: expertises, isLoading: isExpertiseLoading }, image: { data: doctorImage } } = useFactorDoctorData(slug);
  const centersQuery = useQuery([ServerStateKeysEnum.DoctorCenters, slug], () => getDoctorCenters(slug), {
    enabled: !!slug && !userCenterIdProp,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const { data: doctorSlugData } = useQuery(
    ['doctorSlugForFactorImage', slug],
    () => validateDoctorSlug(slug),
    {
      enabled: !!slug && !doctorImage?.image,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 5 * 60 * 1000,
      retry: 2,
    },
  );

  // Same rate line as the profile header (hidden rates / under 5 reviews are not shown). Coming from the
  // profile or search, the rate is already in the cache: show it at once, then confirm from the API.
  const { data: rateDetails } = useQuery(['factorDoctorRate', slug], () => getRateDetailsData({ slug }), {
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: () => cachedRateDetails(queryClient, slug),
  });
  const rate = mapRateDetailsToCardRate(rateDetails);

  // How long the patient can ask: the doctor's own chat lifecycle (Hami), by slug — the same key the
  // profile/search prefetch warms, so it is usually already cached. No value → no line (never a guess).
  const { data: chatLifecycleHours, isLoading: isChatLifecycleLoading } = useChatLifecycleAutoCloseHours({
    enabled: !!slug,
    doctorSlug: slug,
  });
  // Phone call: the doctor's own online channels (`secure_call`), as the receipt and turn card read them.
  const { data: onlineChannels, isLoading: isChannelsLoading } = useDoctorOnlineChannels(slug);
  const hasPhoneCall = !!onlineChannels?.includes('secure_call');

  const isLoading = isFullNameLoading || isExpertiseLoading;
  // Coming from the profile/search, the consult center (and the first free slot under the same key the
  // profile prefetches) is already cached: use it with the URL's service id instead of waiting for the
  // page's availability check; the confirmed ids replace them when it finishes.
  const cachedConsultUserCenterId = (priorDoctorData(queryClient, slug, 'centers') as any[] | undefined)?.find?.(
    (center: any) => String(center?.id) === String(CENTERS.CONSULT),
  )?.user_center_id;
  const consultUserCenterId =
    userCenterIdProp ??
    (cachedConsultUserCenterId != null ? String(cachedConsultUserCenterId) : undefined) ??
    centersQuery.data?.find(center => String(center.id) === String(CENTERS.CONSULT))?.user_center_id;
  const isUserCenterLoading = !consultUserCenterId && !!slug && !userCenterIdProp && centersQuery.isLoading;

  // A/B group (decided once by the page): trust UI for half the patients; the rest keep the live invoice.
  const { isTrustUi, abGroup } = abTest;
  useEffect(() => {
    if (!slug || !abGroup) return;
    try {
      splunkInstance('booking').sendEvent({
        group: 'factor_trust_ab',
        type: 'exposure',
        event: { slug, ab_group: abGroup },
      });
    } catch {
      // analytics must never break the invoice
    }
  }, [slug, abGroup]);

  const { freeturnText, legacyFreeturnText, isFreeturnLoading } = useInvoiceNoticeFreeturn({
    serviceId: serviceId || requestedServiceId || '',
    userCenterId: consultUserCenterId,
    isUserCenterLoading,
  });

  const doctorName = [fullName?.name, fullName?.family].filter(Boolean).join(' ');
  const doctorUserId = (doctorSlugData as DoctorSlugValidationResponse | undefined)?.user_id;
  const doctorAvatar = doctorUserId
    ? picUserImageUrl(doctorUserId)
    : getImageUrl(doctorImage?.image, publicRuntimeConfig.CDN_BASE_URL) || undefined;

  const primaryExpertise = expertises?.[0];
  const expertiseGroupId = Number((primaryExpertise as any)?.groups?.[0]?.id);
  const isDurationRowExpertise = expertiseGroupId === 21 || expertiseGroupId === 47;
  const visitDays = isDurationRowExpertise || chatLifecycleHours == null ? null : chatLifecycleHoursToDisplayDays(chatLifecycleHours);
  const bubblesLoading = isLoading || isFreeturnLoading || (isTrustUi && (isChatLifecycleLoading || isChannelsLoading));
  const messages: { key: string; node: React.ReactNode }[] = [
    { key: 'hello', node: <>سلام، {doctorName} هستم.</> },
    {
      key: 'time',
      node: (
        <>
          نوبت که ثبت شد،{' '}
          <Text className="text-primary" fontWeight="semiBold">
            {freeturnText}
          </Text>{' '}
          در خدمتتان هستم.
        </>
      ),
    },
    ...(visitDays != null
      ? [
          {
            key: 'days',
            node: (
              <>
                تا <Text fontWeight="semiBold">{visitDays.toLocaleString('fa-IR')} روز</Text> بعد هم هر سؤالی داشتید، همین‌جا بپرسید.
              </>
            ),
          },
        ]
      : []),
    ...(hasPhoneCall ? [{ key: 'phone', node: <>اگر لازم شد، تلفنی هم صحبت می‌کنیم.</> }] : []),
  ];
  const expertiseLabel = primaryExpertise
    ? getDisplayDoctorExpertise({
        aliasTitle: primaryExpertise.alias_title ?? '',
        degree: primaryExpertise.degree?.name ?? '',
        expertise: primaryExpertise.expertise?.name ?? '',
      })
    : '';

  return (
    <div data-factor-doctor-notice className="w-full p-3 mb-2 space-y-3 bg-white md:rounded-lg shadow-card md:mb-0 lg:sticky lg:top-6 lg:p-4 ">
      <div data-factor-doctor-card>
      <DoctorInfo
        className="p-4 rounded-lg bg-slate-100"
        avatar={doctorAvatar}
        fullName={doctorName}
        expertise={expertiseLabel}
        isLoading={isLoading}
        extra={
          isTrustUi && rate && (
            <div className="flex items-center gap-1 mt-2 text-xs">
              <span className="h-5 px-2 rounded-full bg-green-600 text-slate-50 font-medium flex items-center">{rate.text}</span>
              <span className="text-slate-600">رضایت ({rate.count.toLocaleString('fa-IR')} نظر)</span>
            </div>
          )
        }
      />
      </div>
      {isTrustUi && bubblesLoading && (
        <div aria-busy="true" aria-label="در حال دریافت پیام پزشک" className="flex flex-col gap-1.5">
          <BubbleSkeleton first className="h-11 w-3/5" />
          <BubbleSkeleton className="h-[72px] w-[88%]" />
          <BubbleSkeleton className="h-11 w-4/5" />
        </div>
      )}
      {isTrustUi && !bubblesLoading && <DoctorMessages messages={messages} />}
      {/* Control group: the live invoice's single bubble, unchanged. */}
      {!isTrustUi && bubblesLoading && (
        <div aria-busy="true" aria-label="در حال دریافت پیام پزشک" className={`relative mr-auto ml-2 h-[104px] w-[92%] animate-pulse rounded-2xl bg-slate-100 motion-reduce:animate-none ${TAIL}`} />
      )}
      {!isTrustUi && !bubblesLoading && (
        <div className={`relative mr-auto ml-2 flex w-fit max-w-[92%] lg:max-w-full flex-col gap-1.5 rounded-2xl bg-slate-100 px-4 py-3 text-right ${TAIL}`}>
          <Text fontSize="sm" as="p">
            سلام. من {doctorName} هستم.
          </Text>
          <Text as="p" fontSize="sm" align="justify" className="leading-6">
            پس از نهایی شدن نوبت،{' '}
            <Text className="text-primary" fontWeight="semiBold">
              {legacyFreeturnText}
            </Text>{' '}
            شما را آنلاین ویزیت خواهم کرد.
          </Text>
        </div>
      )}
    </div>
  );
};

const TAIL =
  'rounded-tl-sm before:absolute before:-left-2 before:top-0 before:h-3 before:w-3 before:bg-slate-100 before:[clip-path:polygon(0_0,100%_0,100%_100%)]';

const BubbleSkeleton = ({ first, className }: { first?: boolean; className: string }) => (
  <div className={`relative mr-auto ml-2 rounded-2xl bg-slate-100 animate-pulse motion-reduce:animate-none ${first ? TAIL : ''} ${className}`} />
);

/** Rate already fetched by the profile (its `rate` piece) or shown on the search card, if any. */
const cachedRateDetails = (queryClient: QueryClient, slug: string): RateDetails | undefined => {
  const decode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const profileRate = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['profilePiece', 'rate'] })
    .filter(query => typeof query.queryKey[2] === 'string' && decode(query.queryKey[2] as string) === decode(slug) && query.state.data)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0]?.state.data as RateDetails | undefined;
  if (profileRate) return profileRate;
  const hit = findSearchDoctor(queryClient, slug);
  const rate = Number(hit?.rate_info?.rate);
  const count = Number(hit?.rate_info?.rates_count);
  if (!Number.isFinite(rate) || !Number.isFinite(count) || count < PROFILE_VISIBLE_RATE_MIN_COUNT) return undefined;
  const r = rate.toFixed(1);
  return {
    averageRates: { average_quality_of_treatment: r, average_doctor_encounter: r, average_explanation_of_issue: r },
    hide_rates: false,
    countOfFeedbacks: count,
  };
};

const BUBBLE_STAGGER_MS = 150;
const TYPING_MS = 1200;

/**
 * The doctor's messages arrive like a chat: each bubble comes in from the left out of a blur, one after
 * another; the last one is preceded by a "typing…" bubble. Reduced motion → all at once, no typing.
 */
const DoctorMessages = ({ messages }: { messages: { key: string; node: React.ReactNode }[] }) => {
  const [reducedMotion] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  const [showLast, setShowLast] = useState(reducedMotion);
  const lead = messages.slice(0, -1);
  const last = messages[messages.length - 1];
  useEffect(() => {
    if (showLast) return;
    const timer = setTimeout(() => setShowLast(true), lead.length * BUBBLE_STAGGER_MS + TYPING_MS);
    return () => clearTimeout(timer);
    // Once per visit: later data (e.g. the phone-call line) just joins, it doesn't replay the typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-1.5">
      {lead.map((message, index) => (
        <ChatBubble key={message.key} first={index === 0} delayMs={index * BUBBLE_STAGGER_MS}>
          {message.node}
        </ChatBubble>
      ))}
      {last && (
        // The last bubble keeps its own height from the first frame (invisible until shown), and the typing
        // bubble sits on top of that same space — so nothing below ever moves.
        <div className="relative">
          <div className={showLast ? '' : 'invisible'}>
            {showLast ? (
              <ChatBubble key={last.key} first={lead.length === 0}>
                {last.node}
              </ChatBubble>
            ) : (
              <ChatBubble first={lead.length === 0} animate={false}>
                {last.node}
              </ChatBubble>
            )}
          </div>
          {!showLast && (
            <div className="absolute top-0 left-0">
              <TypingBubble delayMs={lead.length * BUBBLE_STAGGER_MS} first={lead.length === 0} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TypingBubble = ({ first, delayMs }: { first?: boolean; delayMs: number }) => (
  <div
    role="status"
    aria-label="در حال نوشتن"
    style={{ animationDelay: `${delayMs}ms` }}
    className={`relative mr-auto ml-2 flex w-fit items-center gap-1 rounded-2xl bg-slate-100 px-4 py-4 origin-left animate-chat-bubble-in motion-reduce:animate-none ${first ? TAIL : ''}`}
  >
    {[0, 150, 300].map(dotDelay => (
      <span
        key={dotDelay}
        style={{ animationDelay: `${dotDelay}ms` }}
        className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-typing-dot motion-reduce:animate-none"
      />
    ))}
  </div>
);

/** Doctor's message bubble; only the first one of a group carries the tail. */
const ChatBubble = ({
  first,
  delayMs = 0,
  animate = true,
  children,
}: {
  first?: boolean;
  delayMs?: number;
  animate?: boolean;
  children: React.ReactNode;
}) => (
  <div
    style={animate ? { animationDelay: `${delayMs}ms` } : undefined}
    className={`relative mr-auto ml-2 w-fit max-w-[92%] lg:max-w-full rounded-2xl bg-slate-100 px-4 py-3 text-right origin-left ${animate ? 'animate-chat-bubble-in motion-reduce:animate-none' : ''} ${
      first ? TAIL : ''
    }`}
  >
    <Text as="p" fontSize="sm" align="justify" className="leading-6">
      {children}
    </Text>
  </div>
);

export default DoctorInvoiceNotice;
